import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { emptyIfUndefinedOrNull } from "@common/lib/utilities/rxjs-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { PersonService } from "@org-common/lib/person/person.service";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { map, startWith, switchMap, tap, withLatestFrom } from "rxjs/operators";

@Component({
    selector: "adapt-person-profile-teams",
    templateUrl: "./person-profile-teams.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PersonProfileTeamsComponent extends BaseComponent {
    @Input() public set person(value: Person | undefined) {
        this.person$.next(value);
    }

    public person$ = new BehaviorSubject<Person | undefined>(undefined);
    public teams$: Observable<Team[]>;
    public hasActiveConnection$: Observable<boolean>;

    public teamRoles = new Map<Team, Role[]>();
    public teamCollapseStates?: Record<number, boolean>;

    constructor(
        private personService: PersonService,
        commonIntegratedArchitectureService: CommonIntegratedArchitectureFrameworkService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        const entityChange$ = rxjsBreezeService.entityTypeChanged(RoleConnection).pipe(
            startWith(undefined),
        );

        const setPerson$ = this.person$.pipe(
            emptyIfUndefinedOrNull(),
        );

        this.hasActiveConnection$ = combineLatest([entityChange$, setPerson$]).pipe(
            map(([_change, person]) => person.isActive()),
        );

        this.teams$ = combineLatest([entityChange$, setPerson$]).pipe(
            switchMap(([_change, person]) => this.personService.getActiveTeams(person.personId)),
            tap((teams) => this.teamCollapseStates = Object.fromEntries(teams.map((t) => [t.teamId, true]))),
        );

        const personRoleIds$ = combineLatest([entityChange$, setPerson$]).pipe(
            switchMap(([_change, person]) => this.personService.getActiveRoleConnections(person.personId)),
            map((roleConnections) => new Set(roleConnections.map((rc) => rc.roleId))),
        );

        this.teams$.pipe(
            switchMap((teams) => commonIntegratedArchitectureService.getRoleLocationsForTeamIds(teams.map((t) => t.teamId))),
            withLatestFrom(personRoleIds$),
            this.takeUntilDestroyed(),
        ).subscribe(([roleLocations, personRoleIds]) => {
            this.teamRoles.clear();
            for (const roleLocation of roleLocations) {
                if (roleLocation.team && roleLocation.role && personRoleIds.has(roleLocation.roleId)) {
                    const roles = this.teamRoles.get(roleLocation.team) ?? [];
                    roles.push(roleLocation.role);
                    this.teamRoles.set(roleLocation.team, roles);
                }
            }
        });
    }
}
